// Public API origin for CLI (eis) signal uploads + README/page embeds.
//
// This is NOT how the SPA talks to its own backend — the SPA calls same-origin
// /api/v1 via api/client.ts. This origin is the dedicated PUBLIC API host: the
// former `ace-api` service was merged into `public-api`, one host serving the
// badge/card embeds and the eis upload contract for ace/true/ideal. The viewer
// host is the generic `api.orbitlens.io` (stg: `api.stg.orbitlens.io`); the old
// `ace-api.orbitlens.io` name is retired (pre-launch, no legacy alias).
//
// Per-env via VITE_API_ORIGIN (local → http://localhost:8086 from `make s`, stg
// → https://api.stg.orbitlens.io, prod → https://api.orbitlens.io; all set at
// build time by the Makefile / deploy workflows). The default below is prod,
// matching the eis CLI's baked-in defaultUploadURL — so a build that forgets the
// var degrades to prod, never to a dead host.
const PROD_API_ORIGIN = "https://api.orbitlens.io";

export const apiOrigin = import.meta.env.VITE_API_ORIGIN || PROD_API_ORIGIN;

// POST endpoint a user (or the eis CLI) hits to upload signals.
export const uploadEndpoint = `${apiOrigin}/v1/signals/upload`;

// The eis CLI bakes in the prod host, so on prod the command is clean. Any
// other env reaches its API via the ACE_API_URL override, so we prefix it.
export const eisUploadCommand = (token: string) =>
  `${apiOrigin === PROD_API_ORIGIN ? "" : `ACE_API_URL=${apiOrigin} `}eis analyze --upload --token ${token}`;

// GitHub Actions snippet for the machuz/eis composite action. Same upload
// contract as the CLI, run from CI — code never leaves the runner, only the
// signals do. `fetch-depth: 0` is mandatory: the action reads full git history.
// The token lives in a repo secret (ACE_API_TOKEN), never inline.
//
// On prod the action bakes in the prod host, so the snippet stays clean. Any
// other env needs an explicit `api-url:` line — same prod-vs-override split as
// eisUploadCommand. Takes the origin so both branches are exercisable in tests.
export const buildEisActionSnippet = (origin: string) => {
  const apiUrlLine = origin === PROD_API_ORIGIN ? "" : `\n    api-url: ${origin}`;
  return `- uses: actions/checkout@v4
  with:
    fetch-depth: 0
- uses: machuz/eis@v2
  with:
    token: \${{ secrets.ACE_API_TOKEN }}${apiUrlLine}`;
};

export const eisActionSnippet = () => buildEisActionSnippet(apiOrigin);

// README/page embeds for a member's badge + cards. Same public host as upload so
// users only ever see one origin. These are GET endpoints the backend marks
// Cache-Control: public, max-age=3600 and the host serves them through
// CloudFront, so heavy embed traffic is absorbed at the edge and never fans out
// to the backend (cost stays flat under load).
export const badgeUrl = (account: string, author: string) =>
  `${apiOrigin}/badge/${encodeURIComponent(account)}/${encodeURIComponent(author)}`;

// Org (galaxy) badge: team avg signal + core members. Optional domain and/or
// repo (full_name) scope it — repo wins finer-grained when both are present.
export const teamBadgeUrl = (account: string, domain?: string, repo?: string) => {
  const qs = [
    domain ? `domain=${encodeURIComponent(domain)}` : "",
    repo ? `repo=${encodeURIComponent(repo)}` : "",
  ].filter(Boolean);
  return `${apiOrigin}/badge/${encodeURIComponent(account)}/team${qs.length ? `?${qs.join("&")}` : ""}`;
};

// Profile / repo embed cards, served from the same public host as the badge.
// `path` is the card path under /card, e.g. "<account>/<author>/survival" or
// "<account>/team". Callers pass already-encoded segments.
export const cardUrl = (path: string) => `${apiOrigin}/card/${path}`;

// embedLinkUrl is where an embedded badge/card CLICKS THROUGH to — the "made with
// OrbitLens" attribution that turns a passive README impression into a visit +
// the acquisition loop (a viewer sees a friend's badge → clicks → gets their own).
// Hardcoded to PROD Ace on purpose: the Markdown is pasted into PUBLIC READMEs, so
// it must never carry a localhost/stg host (unlike the env-aware image origin).
// ?ref=embed lets inbound from badges be attributed later.
export const embedLinkUrl = "https://ace.orbitlens.io/?ref=embed";
